import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { datetime } from '../DataGrid/formatters';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Skeleton from '@material-ui/lab/Skeleton';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { organisations as organisationsApi } from '@afosto/contacts/api/Organisations';
import { oauth as oauthApi } from '@afosto/iam/api/Oauth';
import formatCurrency from '@afosto/utils/formatCurrency';
import getContactDisplay from '@afosto/utils/getContactDisplay';
import translations from './translations';
import useIntl from '@afosto/utils-react/intl/useIntl';

const OrderCard = ({ order }) => {
  const history = useHistory();
  const intl = useIntl();
  const [contact, setContact] = useState(null);
  const [isLoadingContact, setIsLoadingContact] = useState(true);
  const [organisation, setOrganisation] = useState(null);
  const [isLoadingOrganisation, setIsLoadingOrganisation] = useState(true);
  const [client, setClient] = useState(null);
  const [isLoadingClient, setIsLoadingClient] = useState(true);
  const { client_id: clientID, contact_id: contactID, organisation_id: organisationID } = order;

  useEffect(() => {
    let isMounted = true;

    const fetchContact = async id => {
      setIsLoadingContact(true);
      try {
        const contactResponse = await contactsApi.getContact(id);
        const { data } = contactResponse;
        if (isMounted) {
          setContact(data);
        }
      } catch (error) {
        if (isMounted) {
          setContact(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingContact(false);
        }
      }
    };

    const fetchOrganisation = async id => {
      setIsLoadingOrganisation(true);
      try {
        const organisationResponse = await organisationsApi.viewOrganisation(id);
        const { data } = organisationResponse;
        if (isMounted) {
          setOrganisation(data);
        }
      } catch (error) {
        if (isMounted) {
          setOrganisation(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingOrganisation(false);
        }
      }
    };

    const fetchClient = async id => {
      setIsLoadingClient(true);
      try {
        const clientResponse = await oauthApi.getClient(id);
        const { data } = clientResponse;
        if (isMounted) {
          setClient(data);
        }
      } catch (error) {
        if (isMounted) {
          setClient(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingClient(false);
        }
      }
    };

    if (contactID) {
      fetchContact(contactID);
    } else {
      setIsLoadingContact(false);
    }

    if (organisationID) {
      fetchOrganisation(organisationID);
    } else {
      setIsLoadingOrganisation(false);
    }

    if (clientID) {
      fetchClient(clientID);
    } else {
      setIsLoadingClient(false);
    }

    return () => {
      isMounted = false;
    };
  }, [clientID, contactID, organisationID]);

  let orderStatus = 'concept';
  let orderStatusColor = 'default';
  if (order?.is_accepted) {
    orderStatus = 'accepted';
    orderStatusColor = 'primary';
  }
  if (order?.is_confirmed) {
    orderStatus = 'confirmed';
  }
  if (order?.is_finished) {
    orderStatus = 'finished';
    orderStatusColor = 'default';
  }

  return (
    <Grid item xs={12}>
      <Card>
        <CardActionArea onClick={() => history.push(`/orders/${order?.id}`)}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="baseline">
              <Typography variant="subtitle1">{order?.number}</Typography>
              <Typography variant="caption" color="textSecondary">
                {datetime(order?.created_at)}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" pt={1} pb={1}>
              <Box minWidth="50%">
                <Typography variant="body2">
                  {isLoadingContact ? (
                    <Skeleton variant="text" width="50%" />
                  ) : (
                    getContactDisplay(contact ?? {})
                  )}
                </Typography>
                <Typography variant="body2">
                  {isLoadingOrganisation ? (
                    <Skeleton variant="text" width="40%" />
                  ) : (
                    organisation?.name
                  )}
                </Typography>
              </Box>
              <Chip
                label={intl.formatMessage(translations.orderCard.status[orderStatus])}
                color={orderStatusColor}
                size="small"
                variant="outlined"
              />
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="baseline">
              <Typography variant="caption" color="textSecondary" style={{ minWidth: '40%' }}>
                {isLoadingClient ? <Skeleton variant="text" /> : client?.name}
              </Typography>
              <Typography variant="subtitle1" align="right">
                {formatCurrency((order?.total ?? 0) / 100)}
              </Typography>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );
};

export const OrderCardSkeleton = () => (
  <Grid item xs={12}>
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="baseline">
          <Typography variant="subtitle1" style={{ width: '40%' }}>
            <Skeleton variant="text" />
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ width: '40%' }}>
            <Skeleton variant="text" />
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" pt={1} pb={1}>
          <Box width="50%">
            <Typography variant="body2">
              <Skeleton variant="text" width="50%" />
            </Typography>
            <Typography variant="body2">
              <Skeleton variant="text" width="40%" />
            </Typography>
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="baseline">
          <Typography variant="caption" color="textSecondary" style={{ width: '40%' }}>
            <Skeleton variant="text" />
          </Typography>
          <Typography variant="subtitle1" align="right" style={{ width: '25%' }}>
            <Skeleton variant="text" />
          </Typography>
        </Box>
      </CardContent>
    </Card>
  </Grid>
);

OrderCard.propTypes = {
  order: PropTypes.object,
};

OrderCard.defaultProps = {
  order: {},
};

export default OrderCard;
